import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';

const LOGO_URL = "/images/93q1mmpr_fesch.png";

const navItems = [
  { name: 'Startseite', href: '#hero' },
  { name: 'Leistungen', href: '#leistungen' },
  { name: '\u00dcber uns', href: '#ueber-uns' },
  { name: 'Referenzen', href: '#referenzen' },
  { name: 'Karriere', href: '/karriere', isPage: true },
  { name: 'Kontakt', href: '#kontakt' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (!isHomePage) {
      window.location.href = '/' + href;
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <header 
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${
          isScrolled ? 'header-scrolled py-3' : 'py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a 
              href="#hero" 
              onClick={(e) => scrollToSection(e, '#hero')}
              data-testid="header-logo"
              className="flex-shrink-0"
            >
              <img 
                src={LOGO_URL} 
                alt="Fesch Elektrotechnik Logo" 
                className={`transition-all duration-300 ${isScrolled ? 'h-10' : 'h-12'}`}
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navItems.map((item) => (
                item.isPage ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    className="font-medium text-neutral-700 hover:text-[#E31E24] transition-colors duration-200 text-sm tracking-wide"
                  >
                    {item.name}
                  </Link>
                ) : (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => scrollToSection(e, item.href)}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    className="font-medium text-neutral-700 hover:text-[#E31E24] transition-colors duration-200 text-sm tracking-wide"
                  >
                    {item.name}
                  </a>
                )
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <a 
                href="tel:020243044510"
                data-testid="header-phone"
                className="flex items-center gap-2 text-neutral-700 hover:text-[#E31E24] transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="font-medium text-sm">0202 / 430 445 10</span>
              </a>
              <a
                href="#kontakt"
                onClick={(e) => scrollToSection(e, '#kontakt')}
                data-testid="header-cta"
                className="bg-[#E31E24] text-white px-6 py-2.5 font-semibold text-sm uppercase tracking-wider hover:bg-[#C41217] transition-all duration-300 hover:-translate-y-0.5"
              >
                Anfrage
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-button"
              className="lg:hidden p-2 text-neutral-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menü öffnen"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
        style={{ zIndex: 105 }}
      />

      {/* Mobile Menu */}
      <div 
        data-testid="mobile-menu"
        className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}
        style={{ zIndex: 110 }}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-8">
            <img src={LOGO_URL} alt="Fesch Elektrotechnik" className="h-10" />
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 text-neutral-900"
              aria-label="Menü schließen"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <nav className="flex flex-col gap-4">
            {navItems.map((item) => (
              item.isPage ? (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-3 text-lg font-medium text-neutral-900 border-b border-zinc-100 hover:text-[#E31E24] transition-colors"
                >
                  {item.name}
                </Link>
              ) : (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className="py-3 text-lg font-medium text-neutral-900 border-b border-zinc-100 hover:text-[#E31E24] transition-colors"
                >
                  {item.name}
                </a>
              )
            ))}
          </nav>

          <div className="mt-8 pt-8 border-t border-zinc-200">
            <a 
              href="tel:020243044510"
              className="flex items-center gap-3 text-neutral-700 mb-4"
            >
              <Phone className="w-5 h-5 text-[#E31E24]" />
              <span className="font-medium">0202 / 430 445 10</span>
            </a>
            <a
              href="#kontakt"
              onClick={(e) => scrollToSection(e, '#kontakt')}
              className="block w-full bg-[#E31E24] text-white text-center py-3 font-semibold uppercase tracking-wider hover:bg-[#C41217] transition-colors"
            >
              Jetzt anfragen
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
